import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, auth } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { Institution } from '../types/models';

const emptyForm = { code: '', name: '' };

export function InstitutionsPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();
  // Only platform admins may create/edit/delete institutions — staff get read-only access.
  const isAdmin = auth.getUser()?.role === 'Admin';

  const [insts, setInsts] = useState<Institution[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      setInsts(await api.listInstitutions(orgId));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load institutions.');
    }
  }

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(i: Institution) {
    setFormError(null); setEditId(i.id); setForm({ code: i.code, name: i.name }); setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      if (editId) await api.updateInstitution(editId, form.code.trim(), form.name.trim());
      else await api.createInstitution({ organizationId: orgId, code: form.code.trim(), name: form.name.trim() });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Institution updated.' : 'Institution created.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save institution.';
      setFormError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function remove(i: Institution) {
    if (!confirm(`Delete institution “${i.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteInstitution(i.id);
      await refresh();
      toast.success('Institution deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete institution.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Institutions</h1>
        {isAdmin && <button className="primary" onClick={openCreate}>+ Add institution</button>}
      </div>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>S.no</th>
              <th>Name</th>
              <th>Code</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!insts && <tr><td colSpan={4} className="muted">Loading…</td></tr>}
            {insts && insts.length === 0 && (
              <tr><td colSpan={4} className="muted">No institutions yet.</td></tr>
            )}
            {insts?.map((i, idx) => (
              <tr key={i.id}>
                <td>{idx + 1}</td>
                <td style={{ fontWeight: 600 }}>{i.name}</td>
                <td className="slug">{i.code}</td>
                <td>
                  {isAdmin && <button className="link-btn" onClick={() => openEdit(i)}>Edit</button>}
                  {isAdmin && <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(i)}>Delete</button>}
                  <RecordInfoButton entity="institutions" id={i.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit institution' : 'Create institution'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 160px' }}>
                <div className="muted">Code *</div>
                <input value={form.code} required maxLength={32} autoFocus
                       onChange={e => setForm({ ...form, code: e.target.value })} placeholder="DIT" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Name *</div>
                <input value={form.name} required maxLength={300}
                       onChange={e => setForm({ ...form, name: e.target.value })}
                       placeholder="Demo Institute of Technology" />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create institution'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
